import { useAppProps } from 'antd/es/app/context';

import API from 'lib/API';

/** 🚧 _To v1 routes, use {@link Options} instead._ */
export type MakeMessage = (response: Service.V2.ServerException) => string | null;

/** 🚧 _To v2 routes, use {@link MakeMessage} instead._ */
export type Options = {
    message?: (message: Service.Message) => Service.Message | Service.Message,
};

/** 🚧 _To v1 routes, use {@link hasServiceError} instead._ */
export function hasServiceErrorV2(
    response:
        | Service.V2.DefaultResponse
        | Service.V2.ExceptionResponse
): response is Service.V2.ExceptionResponse {
    if (response instanceof Error)
        return true;

    if ('error' in response)
        return true;

    // Internal `Request.ts` error handling
    if (Array.isArray(response) && response.length === 0)
        return true;

    return false;
}

/** 🚧 _To v1 routes, use {@link handleServiceError} instead._ */
export function handleServiceErrorV2(
    { notification }: useAppProps, 
    response: Service.V2.ExceptionResponse,
    makeMessage?: MakeMessage
): void {
    // Already handled by `Request.ts` internal error handling, so skip
    if (Array.isArray(response))
        return;

    if (response instanceof Error)
        return notification.error({ message: 'Algo deu errado!', description: response.message });

    if (makeMessage !== undefined)
        return notification.error({ message: 'Algo deu errado!', description: makeMessage(response) ?? response.error.description });

    notification.error({ message: 'Algo deu errado!', description: response.error.description });
}

/** 🚧 _To v2 routes, use {@link hasServiceErrorV2} instead._ */
export function hasServiceError(response: Service.DefaultResponse | Service.ExceptionResponse): response is Service.ExceptionResponse {
    if (!response.success)
        return true;

    return false;
}

/**
 * 🚧 _To v1 routes, use {@link handleServiceErrorV2} instead._
 * 
 * @see https://ant.design/components/app
 */
export function handleServiceError({ notification }: useAppProps, response: Service.ExceptionResponse, options?: Options) {
    // We already show an alert to the user (Request.ts#request#asyncCall)
    if (Array.isArray(response) && response.length === 0)
        return;

    if (!response.message)
        return notification.error({ message: 'Atenção', description: JSON.stringify(response) });

    if (options !== undefined && typeof options.message === 'string')
        return notification.error({ message: 'Atenção', description: options.message });

    if (options !== undefined && typeof options.message === 'function')
        return notification.error({ message: 'Atenção', description: options.message(response.message) });

    notification.error({ message: 'Atenção', description: response.message });
}

/** @factory */
export function makeUploadAction(): string {
    const action = `${API.api_url}/file/upload`;

    return action;
}

/**
 * @example
 * ```ts
 * const route = '/guest';
 *
 * export const findGuest = (
 *   cpf: Guest.Model['cpf'] // '04912171010'
 * ): Promise<FindGuest.Response> => Request.get(makeRoutePath([route, cpf])); // '/guest/04912171010`
 * 
 * ```
 */
export function makeRoutePath(paths: string[]): string {
    return paths.join('/');
}